import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import pl from "dayjs/locale/pl";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, useMemo, useLayoutEffect } from "react";
import { ThemeProvider } from "styled-components";
import { lightTheme, darkTheme } from "./theme";
import { GlobalStyles } from "./components/GlobalStyles";
import { Cookies } from "./components/Cookies";
import { Navbar, WelcomeWizard } from "./components";
import { ThemeProvider as CustomThemeProvider, useThemeMode } from "./context/ThemeProvider";
import { Home } from "./views/Home/Home";
import { NewsPage } from "./views/NewsPage/NewsPage";
import { AboutPage } from "./views/AboutPage/AboutPage";
import { AirQualityPage } from "./views/AirQualityPage/AirQualityPage";
import { SettingsPage } from "./views/SettingsPage/SettingsPage";
import { ScheduleProvider } from "./context/ScheduleProvider";
import { AirQualityProvider } from "./context/AirQualityProvider";
import { Box, ChakraProvider, extendTheme, useColorMode } from "@chakra-ui/react";

import * as Sentry from "@sentry/react";

Sentry.init({
  dsn: "https://f0cc0f387df80e54603d701b5101c812@o4508618705993728.ingest.de.sentry.io/4508618709860432",
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

dayjs.extend(utc);
dayjs.locale(pl);

// Komponent do scrollowania na górę przy zmianie trasy
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant" as ScrollBehavior,
    });
  }, [pathname]);

  return null;
}

const chakraTheme = extendTheme({
  config: {
    initialColorMode: "light",
    useSystemColorMode: false,
  },
});

function AppContent() {
  const { setColorMode } = useColorMode();
  const { themeMode } = useThemeMode();
  const styledTheme = useMemo(() => {
    return themeMode === "light" ? lightTheme : darkTheme;
  }, [themeMode]);

  // Synchronize Chakra UI color mode with our theme - useLayoutEffect to update before render
  useLayoutEffect(() => {
    setColorMode(themeMode);
  }, [themeMode, setColorMode]);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <ThemeProvider theme={styledTheme}>
        <ScheduleProvider>
          <AirQualityProvider>
            <GlobalStyles />
            <Box pb="70px">
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/aktualnosci" element={<NewsPage />} />
                <Route path="/jakosc-powietrza" element={<AirQualityPage />} />
                <Route path="/o-aplikacji" element={<AboutPage />} />
                <Route path="/ustawienia" element={<SettingsPage />} />
              </Routes>
            </Box>
            <Navbar />
            <Cookies />
            <WelcomeWizard />
          </AirQualityProvider>
        </ScheduleProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

function App() {
  return (
    <CustomThemeProvider>
      <AppWrapper />
    </CustomThemeProvider>
  );
}

function AppWrapper() {
  const { themeMode } = useThemeMode();
  
  // Force re-render when theme changes by using themeMode as key
  return (
    <ChakraProvider theme={chakraTheme} key={themeMode}>
      <AppContent />
    </ChakraProvider>
  );
}

export default App;
